import * as Sentry from "@sentry/nuxt"
import { EVENTS, track, numeric, entityId } from "~/utils/analytics"

const getBackendErrorMessage = (error: any, fallbackMessage: string) => {
	const responseData = error?.response?._data ?? error?.response?.data
	const errorData = error?._data ?? error?.data
	const payload = responseData ?? errorData

	if (typeof payload === "string" && payload.trim()) {
		return payload
	}

	if (payload && typeof payload === "object") {
		if (typeof payload.message === "string" && payload.message.trim()) {
			return payload.message
		}
		if (typeof payload.error === "string" && payload.error.trim()) {
			return payload.error
		}
		if (Array.isArray(payload.errors) && payload.errors.length > 0) {
			const firstError = payload.errors[0]
			if (typeof firstError === "string" && firstError.trim()) {
				return firstError
			}
			if (firstError && typeof firstError.message === "string" && firstError.message.trim()) {
				return firstError.message
			}
		}
	}

	if (typeof error?.message === "string" && error.message.trim()) {
		return error.message
	}

	return fallbackMessage
}
const costCentreModule = {
	namespaced: true,
	state: () => ({
		chosenCostCentre: null,
		allCostCentres: [],
		allCostCentresLoading: false,
		allCostCentresPagination: null,
		allCostCentresTotal: 0,
		costCentres: [],
		currentCostCentre: null,
		costCentresLoading: false,
		costCentreError: null,
		newCostCentre: null,
	}),
	mutations: {
		SET_ALL_COST_CENTRES(state, payload) {
			state.allCostCentres = payload || []
		},
		SET_ALL_COST_CENTRES_LOADING(state, payload) {
			state.allCostCentresLoading = payload
		},
		SET_ALL_COST_CENTRES_PAGINATION(state, payload) {
			state.allCostCentresPagination = payload
		},
		SET_ALL_COST_CENTRES_TOTAL(state, payload) {
			state.allCostCentresTotal = payload
		},
		SET_NEW_COST_CENTRE(state, payload) {
			state.newCostCentre = payload
		},
		SET_COST_CENTRES(state, payload) {
			state.costCentres = payload
		},
		SET_COST_CENTRES_LOADING(state, payload) {
			state.costCentresLoading = payload
		},
		SET_CHOSEN_COST_CENTRE(state, payload) {
			state.chosenCostCentre = payload
		},
		SET_COST_CENTRE_ERROR(state, payload) {
			state.costCentreError = payload
		},
		SET_CURRENT_COST_CENTRE(state, payload) {
			state.currentCostCentre = payload
		},
	},
	actions: {
		async getAllCostCentres({ commit, dispatch }, payload = {}) {
			const safePayload = payload || {}
			const filters = Array.isArray(safePayload.filters) ? safePayload.filters : []
			const isActive = safePayload.isActive

			if (isActive !== undefined) {
				filters.push(["is_active", isActive ? 1 : 0, true])
			}
			const filterQuery = generateFilterQuery(filters)

			const page = payload?.page || 1
			const limit = payload?.limit || 20
			const query = `?limit=${limit}&page=${page}${filterQuery}`

			commit("SET_ALL_COST_CENTRES_LOADING", true)

			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/costCentres/${query}`, config)
				.then((response) => {
					commit("SET_ALL_COST_CENTRES", response.costCentres || [])
					commit("SET_ALL_COST_CENTRES_PAGINATION", response.paginate || {})
					commit("SET_ALL_COST_CENTRES_TOTAL", response.total || 0)
					commit("SET_ALL_COST_CENTRES_LOADING", false)
				})
				.catch((error) => {
					commit("SET_ALL_COST_CENTRES_LOADING", false)
					dispatch("handleError", { error }, { root: true })
				})
		},
		async createCostCentre({ commit, dispatch }, payload) {
			commit("SET_COST_CENTRES_LOADING", true)
			commit("SET_COST_CENTRE_ERROR", null)

			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "POST",
				body: JSON.stringify(payload),
			}

			try {
				const response = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/costCentres`,
					config
				)

				track(EVENTS.COST_CENTRE_SAVED, {
					operation: "created",
					cost_centre_id: entityId((response as any)?.id),
					organisation_id: entityId(payload?.organisation_id),
					member_count: Array.isArray(payload?.customerIds)
						? payload.customerIds.length
						: undefined,
					has_responsible_customer: Boolean(payload?.responsible_customer_id),
				})

				commit("SET_NEW_COST_CENTRE", false)
				commit("SET_SUCCESS_MESSAGE", "Cost centre successfully created", { root: true })
				setTimeout(() => commit("SET_SUCCESS_MESSAGE", false, { root: true }), 5000)

				return response
			} catch (error: any) {
				const errorMessage = getBackendErrorMessage(error, "Failed to create cost centre")
				commit("SET_COST_CENTRE_ERROR", errorMessage)

				if (error?.response?.status === 401 || error?.response?.status === 403) {
					dispatch("handleError", { error, description: "create cost centre" }, { root: true })
				} else {
					commit("SET_ERROR_MESSAGE", errorMessage, { root: true })
				}

				Sentry.captureException(error)
				throw new Error(errorMessage)
			} finally {
				commit("SET_COST_CENTRES_LOADING", false)
			}
		},
		async updateCostCentre({ commit, dispatch }, payload) {
			commit("SET_COST_CENTRES_LOADING", true)
			commit("SET_COST_CENTRE_ERROR", false)

			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "PUT",
				body: JSON.stringify({
					name: payload.name,
					responsible_customer_id: payload.responsible_customer_id,
					is_active: payload.is_active,
					customerIds: payload.customerIds || [],
				}),
			}

			try {
				await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/costCentres/${payload.costCentreId}`,
					config
				)

				track(EVENTS.COST_CENTRE_SAVED, {
					operation: "updated",
					cost_centre_id: entityId(payload?.costCentreId),
					member_count: Array.isArray(payload?.customerIds)
						? payload.customerIds.length
						: undefined,
					is_active: Boolean(payload?.is_active),
					has_responsible_customer: Boolean(payload?.responsible_customer_id),
				})

				commit("SET_SUCCESS_MESSAGE", "Cost centre successfully updated", { root: true })
				setTimeout(() => commit("SET_SUCCESS_MESSAGE", false, { root: true }), 5000)

				return true
			} catch (error: any) {
				const errorMessage = getBackendErrorMessage(error, "Failed to update cost centre")
				commit("SET_COST_CENTRE_ERROR", errorMessage)

				if (error?.response?.status === 401 || error?.response?.status === 403) {
					dispatch("handleError", { error, description: "update cost centre" }, { root: true })
				} else {
					commit("SET_ERROR_MESSAGE", errorMessage, { root: true })
				}

				Sentry.captureException(error)
				throw new Error(errorMessage)
			} finally {
				commit("SET_COST_CENTRES_LOADING", false)
			}
		},
	},
	getters: {
		getChosenCostCentre: (state) => state.chosenCostCentre,
		getAllCostCentres: (state) => state.allCostCentres,
		getAllCostCentresLoading: (state) => state.allCostCentresLoading,
		getCostCentres: (state) => state.costCentres,
		getCostCentresLoading: (state) => state.costCentresLoading,
		getCostCentreError: (state) => state.costCentreError,
	},
}
const generateFilterQuery = (filters, date = undefined) => {
	let filterQuery = ""
	if (filters?.length) {
		filterQuery =
			"&filter=" +
			encodeURIComponent(
				JSON.stringify(
					filters.map(([key, value, exact, not]) => {
						return { key, value, exact, not }
					})
				)
			)
	}
	if (date) {
		filterQuery = filterQuery + "&date=" + JSON.stringify(date)
	}
	return filterQuery
}

export default costCentreModule
